const CATEGORY_ICON_MAP = {
  Candidate: "fa-user",
  AI: "fa-microchip",
  Client: "fa-building",
  General: "fa-bell",
};

const normalizeCategory = (value = "") => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw.includes("candidate")) return "Candidate";
  if (raw === "ai" || raw.includes("recruiter")) return "AI";
  if (raw.includes("client")) return "Client";
  return "General";
};

export const looksLikeNotificationHtml = (value) =>
  value != null && /<[a-z][\s\S]*>/i.test(String(value));

/** Parses plain `message` text into company / action / user when possible. */
export const parseNotificationMessageText = (rawMessage = "") => {
  const text = String(rawMessage || "").trim();
  if (!text) return null;

  const feedbackMatch = text.match(/^(.+?)\s+(gave feedback to)\s+(.+)$/i);
  if (feedbackMatch) {
    return {
      company: feedbackMatch[1].trim(),
      action: feedbackMatch[2].trim(),
      user: feedbackMatch[3].trim(),
    };
  }

  return null;
};

export const extractNotificationListFromResponse = (response = {}) => {
  const envelope =
    response?.data != null && typeof response.data === "object" ? response : response ?? {};
  const inner = envelope?.data;

  if (Array.isArray(inner)) {
    return {
      records: inner,
      pageNumber: envelope?.pageNumber,
      pageSize: envelope?.pageSize,
      totalPages: envelope?.totalPages,
      totalRecords: envelope?.totalRecords,
    };
  }

  const nested = inner && typeof inner === "object" ? inner : envelope;
  const records =
    nested?.notifications ??
    nested?.records ??
    (Array.isArray(nested?.data) ? nested.data : Array.isArray(nested) ? nested : []);

  return {
    records: Array.isArray(records) ? records : [],
    pageNumber: nested?.pageNumber ?? envelope?.pageNumber,
    pageSize: nested?.pageSize ?? envelope?.pageSize,
    totalPages: nested?.totalPages ?? envelope?.totalPages,
    totalRecords:
      nested?.totalRecords ??
      envelope?.totalRecords ??
      (Array.isArray(records) ? records.length : 0),
  };
};

/** Maps API notification row → UI row for simple list (no grouping). */
export const mapNotificationApiRow = (item = {}) => {
  const category = normalizeCategory(
    item?.category ?? item?.type ?? item?.notificationType
  );
  const readRaw = item?.read ?? item?.isRead ?? item?.isAcknowledged;
  const isRead =
    readRaw === true ||
    readRaw === "true" ||
    readRaw === 1 ||
    readRaw === "1";

  const rawMessageHtml = String(
    item?.messageHtml ?? item?.messageHTML ?? item?.htmlMessage ?? ""
  ).trim();
  const rawMessage = String(
    item?.message ?? item?.body ?? item?.description ?? item?.content ?? rawMessageHtml
  ).trim();
  const messageIsHtml =
    looksLikeNotificationHtml(rawMessage) || looksLikeNotificationHtml(rawMessageHtml);

  const explicitTitle = String(
    item?.title ?? item?.subject ?? item?.company ?? item?.clientName ?? ""
  ).trim();

  const action = item?.action ?? "";
  const subjectName =
    item?.user ??
    item?.candidateName ??
    item?.candidateDisplayName ??
    item?.userName ??
    "";

  const legacyComposedMessage =
    rawMessage || [action, subjectName].filter(Boolean).join(" ").trim();

  const title = explicitTitle;
  const message =
    explicitTitle && rawMessage && explicitTitle !== rawMessage
      ? rawMessage
      : explicitTitle
        ? ""
        : legacyComposedMessage;

  const createdOn =
    item?.createdOn ?? item?.createdAt ?? item?.createAt ?? item?.sentAt ?? "";
  const createdOnRelative = String(item?.createdOnRelative ?? "").trim();
  const iconRaw = item?.icon;
  const icon =
    iconRaw != null && String(iconRaw).trim() !== ""
      ? String(iconRaw).trim()
      : CATEGORY_ICON_MAP[category] ?? CATEGORY_ICON_MAP.General;

  const parsedMessage = messageIsHtml ? null : parseNotificationMessageText(rawMessage);

  let company = String(
    item?.company ?? item?.clientName ?? item?.clientDisplayName ?? ""
  ).trim();
  let user = String(
    item?.user ??
      item?.candidateName ??
      item?.candidateDisplayName ??
      item?.userName ??
      ""
  ).trim();
  let actionText = String(item?.action ?? "").trim();

  if (!company && parsedMessage?.company) company = parsedMessage.company;
  if (!actionText && parsedMessage?.action) actionText = parsedMessage.action;
  if (!user && parsedMessage?.user) user = parsedMessage.user;

  const hasStructuredCopy = !messageIsHtml && Boolean(company || actionText || user);
  const displayMessage = hasStructuredCopy ? "" : rawMessage;

  return {
    ...item,
    id: item?.id ?? item?._id ?? "",
    title,
    message: displayMessage || message,
    messageIsHtml,
    company: company || explicitTitle,
    action: actionText,
    user,
    link: item?.link ?? null,
    category,
    icon,
    createdAt: createdOn,
    createdOn,
    createdOnRelative,
    timeLabel: createdOnRelative || "",
    read: isRead,
    unread: !isRead,
  };
};

export const mapNotificationApiList = (list = []) =>
  (Array.isArray(list) ? list : []).map(mapNotificationApiRow).filter((row) => row.id);
